import { Menu, Skeleton, TextInput } from "@mantine/core";
import { keepPreviousData } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Check, ChevronDown, Package, Search } from "lucide-react";
import { useState } from "react";

import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Product, ProductOrder } from "../../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../../api/signed-urls";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { useMembership } from "../../../platform/membership";
import { managesBrands } from "../../../platform/permissions";
import { ThumbImage } from "../../../ui/ThumbImage";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import gen from "../generations/generations.module.css";
import home from "../home2.module.css";
import library from "../library/library.module.css";
import { coverOf } from "./cover";
import classes from "./products.module.css";

/** The Products page's orders, in the prototype's words; newest first is both pages' first. */
const ORDERS: readonly { value: ProductOrder; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "a_to_z", label: "A to Z" },
  { value: "z_to_a", label: "Z to A" },
  { value: "most_popular", label: "Most popular" },
];

/** The prototype's "Newest first ▾": the order, as words, opening its choices. */
function OrderMenu({ value, onChange }: { value: ProductOrder; onChange: (next: ProductOrder) => void }) {
  const current = ORDERS.find((each) => each.value === value) ?? ORDERS[0];
  return (
    <Menu position="bottom-start">
      <Menu.Target>
        <button type="button" className={classes.order} aria-label={`Order: ${current?.label}`}>
          {current?.label} <ChevronDown size={14} aria-hidden />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        {ORDERS.map((each) => (
          <Menu.Item
            key={each.value}
            rightSection={each.value === value ? <Check size={16} aria-hidden /> : null}
            onClick={() => onChange(each.value)}
          >
            {each.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function Card({ product, teamId, brandId }: { product: Product; teamId: string; brandId: string }) {
  const cover = coverOf(product);
  const line = product.tagline ?? product.description;
  return (
    // The product's own page, its details, images and edit (decision 30).
    <Link
      to="/teams/$teamId/brands/$brandId/home2/products/$productId"
      params={{ teamId, brandId, productId: product.id }}
      className={home.productCard}
    >
      <span className={home.productMedia}>
        {cover === null ? (
          <Package size={28} strokeWidth={1.5} className={home.mediaIcon} aria-hidden />
        ) : (
          <ThumbImage thumb={cover.thumb} full={cover.full} alt="" />
        )}
      </span>
      <b className={classes.name} title={product.name}>
        {product.name}
      </b>
      {line === null ? null : <span className={classes.line}>{line}</span>}
    </Link>
  );
}

/**
 * The prototype's `#products`: the brand's products on the Products page's own read (its order,
 * its key), searched by name as that page does; a product opens its own page and "Add product"
 * the current form (spec ui-perf/pro-studio-redesign, PR d4, decision 30).
 */
export function ProductsPage() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/home2/products",
  });
  // The brand menu keeps the page mounted: another brand starts the list anew, so no card of the
  // last brand's, linking to its product, shows while the next brand's list loads.
  return <ProductList key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />;
}

/** One brand's products: kept while another order loads (`keepPreviousData`), never a brand. */
function ProductList({ teamId, brandId }: { teamId: string; brandId: string }) {
  const [order, setOrder] = useState<ProductOrder>("newest");
  const [typed, setTyped] = useState("");
  const me = useMembership(teamId);
  // The covers' signed URLs expire, so the list is read again before they do; another order keeps
  // the cards shown until its own list is read.
  const products = useListProducts(
    teamId,
    brandId,
    { order },
    {
      query: {
        ...freshUrls((list: Product[]) => soonestExpiry(list.map((each) => each.urls_expire_at))),
        placeholderData: keepPreviousData,
      },
    },
  );
  const words = typed.trim().toLowerCase();
  const shown = products.data?.filter((product) => product.name.toLowerCase().includes(words));
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Products</h1>
          <p className={page.lede}>Set them up once. Every tool can use them.</p>
        </div>
        {/* As the Products page's "Add Product", for those who manage brands. */}
        {me !== undefined && managesBrands(me.role) ? (
          <Link
            to="/teams/$teamId/brands/$brandId/home2/products/new"
            params={{ teamId, brandId }}
            className={library.primary}
          >
            Add product
          </Link>
        ) : null}
      </div>
      <div className={gen.toolbar}>
        <TextInput
          className={library.search}
          aria-label="Search products"
          placeholder="Search products"
          leftSection={<Search size={16} aria-hidden />}
          value={typed}
          onChange={(event) => setTyped(event.currentTarget.value)}
        />
        <OrderMenu value={order} onChange={setOrder} />
      </div>
      {products.isError ? <ErrorAlert error={products.error} /> : null}
      {shown === undefined ? (
        <div className={home.products}>
          {[0, 1, 2, 3].map((each) => (
            <Skeleton key={each} className={home.productSkeleton} />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className={create.empty}>
          <b>{words === "" ? "No products yet" : "No products found"}</b>
          <span className={home.small}>
            {words === ""
              ? "Add one once and every tool can use it."
              : "Check for typos, or try a different search."}
          </span>
        </div>
      ) : (
        <ul className={`${home.products} ${classes.list}`} aria-label="Products">
          {shown.map((product) => (
            <li key={product.id} className={classes.item}>
              <Card product={product} teamId={teamId} brandId={brandId} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
